import { gql } from '@apollo/client';
import { sortBy, flatten } from 'lodash';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { getTypeNameBySlug } from 'client/site-modules/shared/utils/query-type-mapping';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { GRAPHQL_FRAGMENTS } from 'client/data/constants/graphql-fragments';
import { CAR_TYPES, SUBTYPE_MATCH_WHITELIST } from 'site-modules/shared/constants/upper-funnel/car-types';
import { COMPLEX_TYPE_RANKINGS_QUERIES } from 'client/site-modules/shared/constants/upper-funnel/type-rankings-gql-queries';

export const PAGE_SIZE = {
  SIZE_100: 100,
};

export function buildTypeRankingsPath(type, pageSize = PAGE_SIZE.SIZE_100) {
  return `types["${type}"].pageSize["${pageSize}"]`;
}

export function buildSubTypeRankingsPath(type, subtype, pageSize = PAGE_SIZE.SIZE_100) {
  return `types["${type}"].subtypes["${subtype}"].pageSize["${pageSize}"]`;
}

export function buildTypeRankingsPathForAboveTheFold(type, pageSize = PAGE_SIZE.SIZE_100) {
  return `aboveTheFold.types["${type}"].pageSize["${pageSize}"]`;
}

export function buildTypeRankingsPathForBelowTheFold(type, pageSize = PAGE_SIZE.SIZE_100) {
  return `belowTheFold.types["${type}"].pageSize["${pageSize}"]`;
}

function sortSegments(vehicleEditorialRankings) {
  return vehicleEditorialRankings.map(subSegment => ({
    ...subSegment,
    segmentRatings: sortBy(subSegment.segmentRatings, ['rank', 'modelYear.makeName', 'modelYear.modelName']),
  }));
}

const MODELS_EXCLUSIONS_MAP = {
  [CAR_TYPES.CROSSOVER]: ['Toyota 4Runner', 'Jeep Wrangler', 'Ford Bronco', 'Lexus GX 460'],
  [CAR_TYPES.COMPACT]: ['Jeep Wrangler'],
  [CAR_TYPES.SPORTS_CAR]: ['Honda Civic Coupe'],
  [CAR_TYPES.MIDSIZE]: ['Toyota 4Runner'],
};

const SUBTYPE_REORDER_LIST = [CAR_TYPES.THREEROWSUVS, CAR_TYPES.SMALLSUVS, CAR_TYPES.MIDSIZESUVS];

export const segmentsNameTransformers = {
  [CAR_TYPES.CROSSOVER]: ({ displayName }) => displayName.replace('SUV', 'crossover'),
  [CAR_TYPES.SPORTS_CAR]: {
    coupes: 'Standard sports cars',
    'luxury sport coupes': 'Luxury sports cars',
    'exotic sport coupes': 'Exotic sports cars',
  },
  [CAR_TYPES.COMPACT]: ({ displayName }) =>
    displayName.toLowerCase() === 'hot hatches'
      ? displayName
      : `Compact ${displayName
          .replace('SUV', 'crossover')
          .replace(/(X-)?Small\s?/i, '')
          .toLowerCase()}`,
  [CAR_TYPES.ECONOMY]: ({ displayName }) => `Economy ${displayName.toLowerCase()}`,
  [CAR_TYPES.SUBCOMPACT]: ({ displayName }) =>
    `Subcompact ${displayName
      .replace('SUV', 'crossover')
      .replace(/X-Small\s?/i, '')
      .toLowerCase()}`,
  [CAR_TYPES.MIDSIZE]: ({ displayName }) => displayName.replace('SUV', 'crossover').replace(/midsize/i, 'Mid-size'),
};

export const SUBTYPE_DISPLAY_NAME_EXCLUSIONS = ['hot hatches', 'muscle cars', 'super muscle cars'];

export function sortRankings(rankings) {
  return rankings.sort((a, b) => (a.market.length < b.market.length ? -1 : 1));
}

export function transformSegments(vehicleEditorialRankings, type) {
  const modelsExclusionsList = MODELS_EXCLUSIONS_MAP[type];
  const needsTransformation = modelsExclusionsList || segmentsNameTransformers[type];
  const needsReordered = SUBTYPE_REORDER_LIST.includes(type);

  let finalVehicleEditorialRankings = vehicleEditorialRankings;

  if (needsTransformation) {
    finalVehicleEditorialRankings = vehicleEditorialRankings.map(subSegment => {
      const { displayName } = subSegment;
      const displayNameTransformer = segmentsNameTransformers[type];
      let modelsSuppressed = 0;
      let transformedDisplayName;

      if (displayName && displayNameTransformer) {
        if (typeof displayNameTransformer === 'function') {
          transformedDisplayName = displayNameTransformer({ displayName });
        } else {
          transformedDisplayName = displayNameTransformer[displayName.toLowerCase()];
        }
      }

      return {
        ...subSegment,
        displayName: transformedDisplayName || displayName,
        segmentRatings: modelsExclusionsList
          ? subSegment.segmentRatings.reduce((results, model) => {
              const { modelYear, modelDisplayOverride, rank } = model;
              const { makeName, modelName } = modelYear;
              const vehicleName = `${makeName} ${modelDisplayOverride || modelName}`;
              const isSuppressed = modelsExclusionsList.includes(vehicleName);

              if (isSuppressed) {
                modelsSuppressed += 1;
                return results;
              }
              // Up rank by number of already removed models
              // It's important that models were sorted by rank beforehand
              return [...results, { ...model, rank: rank - modelsSuppressed }];
            }, [])
          : subSegment.segmentRatings,
      };
    });
  }

  if (needsReordered) {
    return sortRankings(finalVehicleEditorialRankings);
  }

  return finalVehicleEditorialRankings;
}

export function getSubtypeMatch(segment, subtypeSlug, typeSlug) {
  const subtypeToLowercase = subtypeSlug.toLowerCase();

  if (typeSlug.includes('sedan')) {
    switch (subtypeToLowercase) {
      case 'sport':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('performance')
        );
      case 'luxury':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('exotic')
        );
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  if (typeSlug.includes('truck')) {
    switch (subtypeToLowercase) {
      case 'small':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('midsize')
        );
      case 'large':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('heavy-duty')
        );
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  if (typeSlug.includes('coupe')) {
    switch (subtypeToLowercase) {
      case 'luxury':
        return (
          segment.displayName.toLowerCase().includes('luxury') || segment.displayName.toLowerCase().includes('exotic')
        );
      case 'sports-cars':
        return (
          segment.displayName.toLowerCase().includes('coupes') && segment.displayName.toLowerCase() !== 'luxury coupes'
        );
      case 'muscle-cars':
        return segment.displayName.toLowerCase().includes('muscle');
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }
  if (typeSlug.includes('hatchback')) {
    switch (subtypeToLowercase) {
      case 'hot-hatches':
        return segment.displayName.toLowerCase() === 'hot hatches';
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  if (typeSlug.includes('electric')) {
    switch (subtypeToLowercase) {
      case 'luxury-electric-cars':
        return (
          segment.displayName.toLowerCase().includes('cars') &&
          (segment.displayName.toLowerCase().includes('luxury') ||
            segment.displayName.toLowerCase().includes('performance') ||
            segment.displayName.toLowerCase().includes('exotic'))
        );
      case 'luxury-electric-suvs':
        return (
          segment.displayName.toLowerCase().includes('suvs') &&
          (segment.displayName.toLowerCase().includes('luxury') ||
            segment.displayName.toLowerCase().includes('performance') ||
            segment.displayName.toLowerCase().includes('exotic'))
        );
      case 'awd':
        return (
          segment.displayName.toLowerCase().includes('all-wheel-drive') ||
          segment.displayName.toLowerCase().includes('performance')
        );
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  if (typeSlug.includes('hybrid')) {
    switch (subtypeToLowercase) {
      case 'luxury':
        return segment.displayName.toLowerCase().includes('luxury');
      case 'plug-in-hybrid-cars':
        return segment.displayName.toLowerCase().includes('plug-in hybrid cars');
      case 'plug-in-hybrid-suvs':
        return segment.displayName.toLowerCase().includes('plug-in hybrid suvs');
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  if (typeSlug.includes('convertible')) {
    switch (subtypeToLowercase) {
      case 'cars':
        return segment.displayName.toLowerCase().includes('convertibles');
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  /**
   * At the bottom bc 'suv' typeSlug is most "generic"
   */
  if (typeSlug.includes('suv')) {
    switch (subtypeToLowercase) {
      case '3-row':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('large')
        );
      case 'luxury':
        return (
          segment.displayName.toLowerCase().includes(subtypeToLowercase) ||
          segment.displayName.toLowerCase().includes('performance')
        );
      default:
        return segment.displayName.toLowerCase().includes(subtypeToLowercase);
    }
  }

  return segment.displayName.toLowerCase().indexOf(subtypeToLowercase) > -1;
}

export function getTypeMatch(segment, typeSlug) {
  const typeSlugSplitToLowercase = SUBTYPE_DISPLAY_NAME_EXCLUSIONS.includes(segment.displayName.toLowerCase())
    ? typeSlug.split(',')[1].replace(/-/g, ' ').toLowerCase()
    : typeSlug.split(',')[0].toLowerCase();

  return segment.displayName.toLowerCase().includes(typeSlugSplitToLowercase);
}

export const getFilteredTypeMatch = (segment, typeSlug) => {
  const typeSlugSplit = typeSlug.split(',')[0].toLowerCase();
  const whitelistMatch = SUBTYPE_MATCH_WHITELIST[typeSlugSplit];

  if (whitelistMatch) {
    return whitelistMatch.includes(segment.displayName.toLowerCase());
  }

  return false;
};

export function getFilteredTypeSegments(filteredSegments, typeSlug, subtypeSlug) {
  return filteredSegments.filter(segment => {
    const typeMatch = getTypeMatch(segment, typeSlug);
    const subtypeMatch = getSubtypeMatch(segment, subtypeSlug, typeSlug);

    return (subtypeMatch && typeMatch) || (subtypeMatch && getFilteredTypeMatch(segment, typeSlug));
  });
}

function resolveSegmentsRankingsPath(match, context) {
  const { typeSlug, pageSize, rankRangeEnd, isElectric } = match;
  const typeName = getTypeNameBySlug(typeSlug);

  return withMetrics(EdmundsGraphQLFederation, context)
    .query(
      COMPLEX_TYPE_RANKINGS_QUERIES[typeSlug] ||
        gql`
          ${GRAPHQL_FRAGMENTS.rangeFields}
          ${GRAPHQL_FRAGMENTS.editorialSegmentFieldsFull}
          query ($category: String!, $rankRangeStart: Int, $rankRangeEnd: Int, $pageSize: Int, $isElectric: Boolean!) {
            vehicleEditorialRankings(edmundsTypeCategory: $category) {
              ...editorialSegmentFieldsFull
            }
          }
        `,
      {
        category: typeName,
        rankRangeStart: 1,
        rankRangeEnd,
        pageSize: parseInt(pageSize, 10),
        isElectric,
      }
    )
    .then(results => {
      const segments = flatten(Object.values(results));
      return sortSegments(segments).filter(({ segmentRatings }) => segmentRatings.some(({ rank }) => !!rank));
    });
}

export const TypeRankingsModel = createModelSegment('typeRankings', [
  {
    path: 'aboveTheFold.types["{typeSlug}"].pageSize["{pageSize}"]',
    resolve({ typeSlug, pageSize }, context) {
      const resolvePath = `types["${typeSlug}"].pageSize["${pageSize}"]`;
      return context.resolveValue(`${resolvePath}`).then(typeRankingsData => {
        const aboveTheFoldData = [typeRankingsData[0]];
        return aboveTheFoldData;
      });
    },
  },
  {
    path: 'belowTheFold.types["{typeSlug}"].pageSize["{pageSize}"]',
    resolve({ typeSlug, pageSize }, context) {
      const resolvePath = `types["${typeSlug}"].pageSize["${pageSize}"]`;
      return context.resolveValue(`${resolvePath}`).then(typeRankingsData => typeRankingsData.slice(1));
    },
  },
  /**
   * @see buildTypeRankingsPath
   */
  {
    path: 'types["{typeSlug}"].pageSize["{pageSize}"]',
    resolve({ typeSlug, pageSize }, context) {
      return resolveSegmentsRankingsPath(
        { typeSlug, pageSize, rankRangeEnd: 10, isElectric: typeSlug === CAR_TYPES.ELECTRIC },
        context
      ).then(results => transformSegments(results, typeSlug));
    },
  },
  /**
   * @see buildSubTypeRankingsPath
   */
  {
    path: 'types["{typeSlug}"].subtypes["{subtypeSlug}"].pageSize["{pageSize}"]',
    resolve(match, context) {
      return resolveSegmentsRankingsPath({ ...match, rankRangeEnd: 25, isElectric: true }, context).then(results => {
        const filteredSegments = getFilteredTypeSegments(results, match.typeSlug, match.subtypeSlug);
        return transformSegments(filteredSegments, match.typeSlug);
      });
    },
  },
]);
